import type { Cmyk, Color, GradientStop, Paint } from '@flobi/artwork-core';
import { useEffect, useRef, useState } from 'react';
import { colorToHex, rgba } from '../../theme';
import { ColourPanel, hexFromTyped } from '../chrome/Colour';
import { Button, ColorWell, EmptyAction, IconButton, NumberField, Section, SegmentedControl } from '../../ui/controls';
import { Menu, type MenuEntry } from '../../ui/menu';
import { addStop, moveStop, removeStop, reverseStops, setStopColor } from './gradientStops';
import { Popover, useAnchor } from '../../ui/popover';
import { tip } from '../../ui/tooltip';
import { beginHold } from '../../ui/hold';
import { usePress } from '../press';
import { colourFrom, inkLabel, inkOf, inkShort, shownColour, shownHex } from '../ink';
import { gradientLine } from '../chrome/colourChoice';
import { ImageFillPicker, imageSwatch, usePaintImages, videoSwatch } from './PaintImages';

/**
 * Paint: a colour well that opens the picker, and the control that chooses what a shape
 * is filled or outlined with — nothing, a colour, or one of the three gradients.
 *
 * Every well opens the SAME picker, the one the colour panel has always been, hanging
 * from the well that asked for it. A colour is a colour wherever it is set, and a second
 * picker for "the gradient's second stop" would be a second thing to learn.
 *
 * In a CMYK file every well, swatch and gradient bar here shows its colour as it will
 * print (the press from `usePress`), and a colour carries its ink (`cmyk`) with it.
 */

/** A colour well with its picker. `hex` null draws the none slash. */
export function ColourField({
  hex,
  ink,
  alpha = 1,
  label,
  title,
  onChange,
  onNone,
  onShader,
  size = 'regular',
  placement = 'left',
  onAlpha,
}: {
  hex: string | null;
  /** The ink the colour was typed as, in a CMYK file. */
  ink?: Cmyk;
  alpha?: number;
  label: string;
  /** What the picker says it is colouring: "Fill", "Stroke", "Stop 2"… */
  title: string;
  /** The colour chosen, and in a CMYK file the ink it stands for. */
  onChange: (hex: string, ink?: Cmyk) => void;
  onNone?: () => void;
  onShader?: () => void;
  size?: 'small' | 'regular';
  /** Which side of the well the picker opens on: away from the panel or bar it sits in. */
  placement?: 'left' | 'right' | 'below' | 'above';
  /** Sets the colour's opacity; without it the picker shows none. */
  onAlpha?: (alpha: number) => void;
}) {
  const [anchor, setAnchor] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  const press = usePress();
  return (
    <>
      <ColorWell ref={setAnchor} colour={hex === null ? null : shownHex(press, hex, ink)} alpha={alpha} label={label} active={open} size={size} onClick={() => setOpen((was) => !was)} />
      <Popover open={open} anchor={anchor} onClose={() => setOpen(false)} placement={placement} align="center" width={304} label={`${title} colour`} ownsKeyboard>
        <ColourPanel
          title={title}
          onClose={() => setOpen(false)}
          value={hex ?? '#000000'}
          ink={ink}
          alpha={alpha}
          onAlpha={onAlpha}
          showing={hex !== null}
          onChange={onChange}
          onNone={onNone}
          onShader={onShader ? () => { onShader(); setOpen(false); } : undefined}
        />
      </Popover>
    </>
  );
}

type Kind = 'solid' | 'linear' | 'radial' | 'conic';

/** What every paint carries besides its own kind, and what a new one is born with. */
const COMMON = { visible: true, opacity: 1, blendMode: 'normal' } as const;

const KIND_NAMES: Record<Kind, string> = { solid: 'Solid', linear: 'Linear', radial: 'Radial', conic: 'Angular' };

/**
 * What a shape is filled (or outlined) with — Figma's way.
 *
 * One row: a well that shows the paint itself (a colour, or the gradient), what it is (the
 * hex, or "Linear"), how opaque, and a minus to take it away. Everything else lives in the
 * picker the well opens: the kind as four marks, and for a gradient ONE bar with the stops
 * sitting on it — drag a stop along it, click the bar for a new one, drag a stop off it (or
 * press Delete) to remove it — and the one picker for whichever stop is chosen.
 *
 * Positions are in the shape's own box, 0 to 1, which is what keeps a gradient where you
 * put it when the shape is resized.
 */
export function PaintControl({
  label,
  paint,
  onChange,
  onClear,
  onShader,
  onPicking,
  stackActions,
  inlinePicker,
}: {
  label: 'Fill' | 'Stroke';
  paint: Paint | undefined;
  onChange: (paint: Paint) => void;
  onClear: () => void;
  onShader?: () => void;
  /** Told when the picker opens and closes, so the canvas can show a gradient's handles. */
  onPicking?: (open: boolean) => void;
  stackActions?: readonly MenuEntry[];
  /** A toolbar stack edits the chosen paint inline instead of opening another picker. */
  inlinePicker?: { selected: boolean; controls: string; onSelect: () => void };
}) {
  const [anchor, setAnchor] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useAnchor<HTMLButtonElement>();
  const [menuOpen, setMenuOpen] = useState(false);
  const press = usePress();
  const images = usePaintImages();
  const picking = useRef(onPicking);
  picking.current = onPicking;
  useEffect(() => {
    picking.current?.(open);
    return () => picking.current?.(false);
  }, [open]);
  const stops = paint && 'stops' in paint ? ((paint as { stops: GradientStop[] }).stops ?? []) : [];
  const clip = paint ? videoSwatch(paint, images) : undefined;
  const kind: Kind | null = paint && (paint.type === 'solid' || paint.type === 'linear' || paint.type === 'radial' || paint.type === 'conic') ? paint.type : null;

  if (!paint) {
    return (
      <EmptyAction icon="plus" onClick={() => onChange({ ...COMMON, type: 'solid', color: { r: 0.37, g: 0.45, b: 0.64, a: 1 } })}>Add {label.toLowerCase()}</EmptyAction>
    );
  }

  // Taking the paint away closes its picker. With no paint the control returns before the
  // Popover, so `open` stayed true and the next Add Fill opened the picker by itself
  // (QA 2026-09-24, paint-F31).
  const clear = () => {
    setOpen(false);
    onClear();
  };
  const opacity = paint.type === 'solid' ? (paint.color.a ?? 1) * (paint.opacity ?? 1) : (paint.opacity ?? 1);
  const setOpacity = (value: number) =>
    onChange(paint.type === 'solid' ? { ...paint, opacity: 1, color: { ...paint.color, a: value / 100 } } : ({ ...paint, opacity: value / 100 } as Paint));
  const openPicker = () => inlinePicker ? inlinePicker.onSelect() : setOpen(true);

  return (
    <div className={`paint-control ${stackActions ? 'paint-stack-row' : ''} ${paint.visible === false ? 'paint-hidden' : ''}`}>
      <button
        ref={setAnchor}
        type="button"
        aria-label={`${label}: ${kind ? KIND_NAMES[kind] : paint.type === 'image' ? paint.resultSet ? paint.resultSet.kind === 'video' ? 'Video set' : 'Image set' : 'Image' : 'Shader'}`}
        aria-haspopup={inlinePicker ? undefined : 'dialog'}
        aria-expanded={inlinePicker ? undefined : open}
        aria-pressed={inlinePicker?.selected}
        aria-controls={inlinePicker?.controls}
        onClick={() => inlinePicker ? inlinePicker.onSelect() : setOpen((was) => !was)}
        {...tip(inlinePicker ? `Edit this ${label.toLowerCase()} layer` : `${label} — open the picker`)}
        className={`paint-swatch relative shrink-0 overflow-hidden transition-shadow ${open || inlinePicker?.selected ? 'ring-[2px] ring-accent/55' : ''}`}
        style={{ background: imageSwatch(paint, images) ?? (clip ? 'var(--fill-2)' : swatchOf(paint, stops, (color) => shownColour(press, color))), boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.25)' }}
      >{clip && <video key={clip} src={`${clip}#t=0.1`} muted playsInline preload="metadata" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full rounded-[inherit] object-cover" />}</button>
      {paint.type === 'solid' && press ? (
        // In a CMYK file a colour reads as its ink; the inks are typed in the picker.
        <button type="button" onClick={openPicker} {...tip(`${label} ink — ${inkLabel(inkOf(press, paint.color))}`)} className="paint-value tnum h-[32px] min-w-0 flex-1 truncate rounded-[7px] bg-fill-1 px-[8px] text-left text-[12px] text-label">
          {inkShort(inkOf(press, paint.color))}
        </button>
      ) : paint.type === 'solid' ? (
        <HexField hex={colorToHex(paint.color)} onChange={(hex) => onChange({ ...paint, color: { ...rgba(hex), a: paint.color.a ?? 1 } })} label={`${label} hex`} />
      ) : (
        <button type="button" onClick={openPicker} className="paint-value h-[32px] min-w-0 flex-1 truncate rounded-[7px] bg-fill-1 px-[8px] text-left text-[12px] text-label">
          {kind ? KIND_NAMES[kind] : paint.type === 'image' ? paint.resultSet ? paint.resultSet.kind === 'video' ? 'Video set' : 'Image set' : 'Image' : 'Shader'}
        </button>
      )}
      <NumberField className="paint-opacity" label={`${label} opacity`} value={Math.round(opacity * 100)} suffix="%" min={0} max={100} decimals={0} onChange={setOpacity} />
      {stackActions ? <>
        <IconButton icon={paint.visible === false ? 'eye-off' : 'eye'} label={`${paint.visible === false ? 'Show' : 'Hide'} ${label}`} size={22} onClick={() => onChange({ ...paint, visible: paint.visible === false })} />
        <IconButton ref={setMenuAnchor} icon="more" label={`${label} actions`} size={22} onClick={() => setMenuOpen(was => !was)} />
        <Menu open={menuOpen} anchor={menuAnchor} onClose={() => setMenuOpen(false)} placement="left" label={`${label} actions`} items={[...stackActions, { kind: 'separator' }, { id: 'remove', label: `Remove ${label.toLowerCase()}`, icon: 'minus', onSelect: clear }]} />
      </> : <IconButton icon="minus" label={`Remove ${label}`} size={24} onClick={clear} />}
      <Popover open={open} anchor={anchor} onClose={() => setOpen(false)} placement="left" align="start" width={304} label={`${label} paint`} ownsKeyboard>
        <PaintPicker label={label} paint={paint} kind={kind} stops={stops} onChange={onChange} onShader={onShader} />
      </Popover>
    </div>
  );
}

/** Stored bottom-to-top by the renderer; displayed with the topmost paint first. */
export function PaintStack({ label, paints, onChange, onPicking, accessory, children, onClose }: {
  label: 'Fill' | 'Stroke'; paints: readonly Paint[]; onChange: (paints: Paint[]) => void;
  onPicking?: (index: number | null) => void;
  accessory?: import('react').ReactNode;
  children?: import('react').ReactNode;
  /** In a dock popover, this stack owns the single heading and close control. */
  onClose?: () => void;
}) {
  const move = (index: number, to: number) => {
    onPicking?.(null);
    const next = [...paints];
    next.splice(to, 0, next.splice(index, 1)[0]!);
    onChange(next);
  };
  const add = () => {
    onPicking?.(null);
    onChange([...paints, { ...COMMON, type: 'solid', color: { r: 0, g: 0, b: 0, a: paints.length ? 0.2 : 1 } }]);
  };
  const close = onClose ? <IconButton icon="close" label={`Close ${label.toLowerCase()}`} onClick={onClose} /> : null;
  const empty = <EmptyAction icon="plus" onClick={add}>Add {label.toLowerCase()}</EmptyAction>;
  return <Section title={label} revealKey={paints.length} collapsible={!onClose}
    emptyAction={!paints.length ? (onClose ? <div className="flex items-center gap-[8px]">{empty}{close}</div> : empty) : undefined}
    accessory={<>{accessory}<IconButton icon="plus" label={`Add ${label}`} disabled={paints.length >= 64} onClick={add} />{close}</>}>
    {paints.map((paint, index) => ({ paint, index })).reverse().map(({ paint, index }) => <div key={index} role="group" aria-label={`${label} ${paints.length - index}`}>
      <PaintControl label={label} paint={paint}
        onChange={next => onChange(paints.map((one, i) => i === index ? next : one))}
        onClear={() => { onPicking?.(null); onChange(paints.filter((_, i) => i !== index)); }}
        onPicking={open => onPicking?.(open ? index : null)}
        stackActions={[
          { id: 'up', label: 'Move up', icon: 'bring-forward', disabled: index === paints.length - 1, onSelect: () => move(index, index + 1) },
          { id: 'down', label: 'Move down', icon: 'send-backward', disabled: index === 0, onSelect: () => move(index, index - 1) },
          { id: 'duplicate', label: 'Duplicate', icon: 'copy', disabled: paints.length >= 64, onSelect: () => { onPicking?.(null); const next = [...paints]; next.splice(index + 1, 0, structuredClone(paint)); onChange(next); } },
        ]} />
    </div>)}
    {children}
  </Section>;
}

/**
 * A small well for a paint that is more than a colour — a gradient, a shader — drawn as
 * the paint itself and opening the whole paint picker. The quick bar's colour wells showed
 * a gradient as "none" and a colour picked there flattened it to a solid
 * (QA 2026-09-24, paint-F12).
 */
export function PaintWell({
  label,
  accessibleLabel = label,
  paint,
  onChange,
  onShader,
  placement = 'below',
}: {
  label: 'Fill' | 'Stroke';
  accessibleLabel?: string;
  paint: Paint;
  onChange: (paint: Paint) => void;
  onShader?: () => void;
  placement?: 'left' | 'right' | 'below' | 'above';
}) {
  const [anchor, setAnchor] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  const press = usePress();
  const images = usePaintImages();
  const stops = 'stops' in paint ? ((paint as { stops: GradientStop[] }).stops ?? []) : [];
  const kind: Kind | null = paint.type === 'solid' || paint.type === 'linear' || paint.type === 'radial' || paint.type === 'conic' ? paint.type : null;
  return (
    <>
      <button
        ref={setAnchor}
        type="button"
        aria-label={accessibleLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...tip(accessibleLabel)}
        onClick={() => setOpen((was) => !was)}
        className={`relative size-[20px] shrink-0 overflow-hidden rounded-[6px] ${open ? 'ring-[2.5px] ring-accent ring-offset-[1.5px] ring-offset-[var(--glass-thick)]' : ''}`}
        style={{ background: imageSwatch(paint, images) ?? swatchOf(paint, stops, (color) => shownColour(press, color)), boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.2), 0 0 0 0.5px var(--glass-border)' }}
      />
      <Popover open={open} anchor={anchor} onClose={() => setOpen(false)} placement={placement} align="center" width={304} label={`${label} paint`} ownsKeyboard>
        <PaintPicker label={label} paint={paint} kind={kind} stops={stops} onChange={onChange} onShader={onShader} />
      </Popover>
    </>
  );
}

/** A live paint sample for the contextual toolbar, including gradients and print colours. */
export function PaintPreview({ paint }: { paint?: Paint }) {
  const press = usePress();
  const images = usePaintImages();
  const background = paint ? imageSwatch(paint, images) ?? swatchOf(paint, 'stops' in paint ? paint.stops : [], color => shownColour(press, color)) : 'linear-gradient(135deg, transparent 46%, var(--label-2) 47%, var(--label-2) 53%, transparent 54%)';
  return <span aria-hidden className="selection-paint-preview" style={{ background }} />;
}

/** Full paint controls embedded directly in a toolbar popover. */
export function InlinePaintPicker({ label, paint, onChange }: { label: 'Fill' | 'Stroke'; paint: Paint; onChange: (paint: Paint) => void }) {
  const kind = paint.type === 'solid' || paint.type === 'linear' || paint.type === 'radial' || paint.type === 'conic' ? paint.type : null;
  const stops = 'stops' in paint ? paint.stops : [];
  return <PaintPicker label={label} paint={paint} kind={kind} stops={stops} onChange={onChange} />;
}

/**
 * The picker the well opens: the colour picker, led by the paint's kind — Solid or Gradient,
 * two words where four unlabelled marks were — and for a gradient its own row (Linear,
 * Radial, Angular; where the chosen stop sits; flip; turn) and the bar of stops, above the
 * square. The square, the opacity and the swatches then set whichever stop is chosen.
 */
function PaintPicker({
  label,
  paint,
  kind,
  stops,
  onChange,
  onShader,
}: {
  label: 'Fill' | 'Stroke';
  paint: Paint;
  kind: Kind | null;
  stops: GradientStop[];
  onChange: (paint: Paint) => void;
  onShader?: () => void;
}) {
  const [chosen, setChosen] = useState(0);
  const images = usePaintImages();
  const [imageDraft, setImageDraft] = useState(false);
  const imageMode = paint.type === 'image' || imageDraft;
  const allowImage = label === 'Fill' && images?.enabled;
  const at = Math.min(chosen, Math.max(0, stops.length - 1));
  /** The gradient Gradient goes back to: the one this paint was, else Linear. */
  const [lastGradient, setLastGradient] = useState<Exclude<Kind, 'solid'>>(kind && kind !== 'solid' ? kind : 'linear');
  const gradient = kind !== null && kind !== 'solid';
  /**
   * None hides the paint rather than taking it away, and the picker stays open: None again,
   * or any colour, brings it back. It used to remove the paint and close the picker, and a
   * shape with nothing else on it was then gone from under the hand (Tarek, 2026-10-01:
   * "if I press None there's no way for me to get it back"). Taking a paint away is the
   * row's minus, or Remove in its menu.
   */
  const showing = paint.visible !== false;

  /** The colour a change of kind starts from, so switching never loses what was chosen. */
  const seed = (): Color => (paint.type === 'solid' ? paint.color : (stops[0]?.color ?? { r: 0.37, g: 0.45, b: 0.64, a: 1 }));
  const keep = () => ({ visible: paint.visible ?? true, opacity: paint.opacity ?? 1, blendMode: paint.blendMode ?? 'normal' });
  const become = (next: Kind) => {
    if (next === kind) return;
    const from = seed();
    const kept = keep();
    if (next !== 'solid') setLastGradient(next);
    // A solid says its opacity in its colour's alpha, so the paint's opacity moves there;
    // visibility and blend stay as they were. A half-clear gradient came out fully opaque,
    // a hidden one showed again and its blend went back to Normal (QA 2026-09-24, paint-F14).
    if (next === 'solid') return onChange({ ...kept, opacity: 1, type: 'solid', color: { ...from, a: (from.a ?? 1) * kept.opacity } });
    // A gradient made from a flat colour runs from that colour to clear, the one default
    // that never looks like a mistake.
    const made: GradientStop[] = stops.length >= 2 ? stops : [{ offset: 0, color: from }, { offset: 1, color: { ...from, a: 0 } }];
    if (next === 'linear') onChange({ ...kept, type: 'linear', stops: made, start: { x: 0, y: 0.5 }, end: { x: 1, y: 0.5 } });
    if (next === 'radial') onChange({ ...kept, type: 'radial', stops: made, center: { x: 0.5, y: 0.5 }, radius: { x: 0.5, y: 0.5 }, rotation: 0 });
    if (next === 'conic') onChange({ ...kept, type: 'conic', stops: made, center: { x: 0.5, y: 0.5 }, rotation: 0 });
  };
  const setStops = (next: GradientStop[]) => onChange({ ...paint, stops: next } as Paint);
  const stop = stops[at];
  /** One of the brand's gradients, laid across the box at its own angle. */
  const brandGradient = (made: { from: string; to: string; angle: number }) => {
    onChange({ ...keep(), type: 'linear', stops: [{ offset: 0, color: colourFrom(made.from, undefined, 1) }, { offset: 1, color: colourFrom(made.to, undefined, 1) }], ...gradientLine(made.angle) });
    setLastGradient('linear');
    setChosen(0);
  };

  const header = (
    <SegmentedControl
      size="small"
      label={`${label} kind`}
      value={imageMode ? 'image' : kind === null ? null : gradient ? 'gradient' : 'solid'}
      onChange={(next) => { setImageDraft(next === 'image'); if (next !== 'image') become(next === 'solid' ? 'solid' : lastGradient); }}
      segments={[
        { value: 'solid', label: 'Solid' },
        { value: 'gradient', label: 'Gradient' },
        ...(allowImage ? [{ value: 'image', label: 'Image' }] : []),
      ]}
      className={allowImage ? 'w-[208px]' : 'w-[150px]'}
    />
  );
  const above =
    gradient && stop ? (
      <div className="grid grid-cols-[minmax(0,1fr)] gap-[8px]">
        <div className="flex items-center gap-[6px]">
          <SegmentedControl
            size="small"
            label="Gradient"
            value={kind}
            onChange={become}
            segments={[
              { value: 'linear', icon: 'gradient-linear', tip: 'Linear' },
              { value: 'radial', icon: 'gradient-radial', tip: 'Radial' },
              { value: 'conic', icon: 'gradient-conic', tip: 'Angular' },
            ]}
            className="w-[96px]"
          />
          <NumberField className="w-[66px]" label="Stop position" value={Math.round(stop.offset * 100)} suffix="%" min={0} max={100} decimals={0} onChange={(value) => {
            const moved = moveStop(stops, at, value / 100);
            setStops(moved.stops);
            setChosen(moved.index);
          }} />
          <span className="flex-1" />
          <IconButton icon="flip-h" label="Flip — run the colours the other way" size={24} onClick={() => setStops(reverseStops(stops))} />
          <IconButton icon="rotate" label="Rotate 90°" size={24} onClick={() => onChange(rotated(paint))} />
        </div>
        <div>
          <GradientBar
            stops={stops}
            chosen={at}
            onChoose={setChosen}
            onChange={(next, index) => {
              setStops(next);
              setChosen(index);
            }}
          />
        </div>
      </div>
    ) : null;

  // The opacity is the chosen stop's in a gradient, and the colour's own in a solid — where
  // the row's % keeps it too, with the paint's opacity left at 1.
  const alpha = stop ? (stop.color.a ?? 1) : paint.type === 'solid' ? (paint.color.a ?? 1) * (paint.opacity ?? 1) : 1;
  const onAlpha = stop
    ? (value: number) => setStops(setStopColor(stops, at, { ...stop.color, a: value }))
    : paint.type === 'solid'
      ? (value: number) => onChange({ ...paint, opacity: 1, color: { ...paint.color, a: value } })
      : undefined;

  if (imageMode && images) return <div className="grid gap-[10px] p-[12px]">
    <div className="flex min-h-[26px] items-center gap-[8px]">
      {header}<span className="flex-1" />
      <Button size="small" variant={showing ? 'plain' : 'prominent'} aria-pressed={!showing} onClick={() => onChange({ ...paint, visible: !showing })}>None</Button>
    </div>
    <ImageFillPicker paint={paint} onChange={(next) => { setImageDraft(false); onChange(next); }} />
  </div>;

  return (
    <>
      {paint.type === 'shader' ? <p className="px-[12px] pt-[12px] text-[11px] text-label-2">A shader from Lumens. Choose a kind to replace it.</p> : null}
      <ColourPanel
        header={header}
        above={above}
        value={stop ? colorToHex(stop.color) : paint.type === 'solid' ? colorToHex(paint.color) : '#000000'}
        ink={stop ? stop.color.cmyk : paint.type === 'solid' ? paint.color.cmyk : undefined}
        alpha={alpha}
        onAlpha={onAlpha}
        showing={showing}
        onChange={(hex, ink) => {
          // The whole colour is replaced: an ink it was typed as goes unless a new one comes.
          // A colour chosen after None shows the paint again.
          if (stop) onChange({ ...paint, visible: true, stops: setStopColor(stops, at, colourFrom(hex, ink, stop.color.a ?? 1)) } as Paint);
          else if (paint.type === 'solid') onChange({ ...paint, visible: true, color: colourFrom(hex, ink, paint.color.a ?? 1) });
          else onChange({ ...COMMON, type: 'solid', color: colourFrom(hex, ink, 1) });
        }}
        onBrandGradient={gradient ? brandGradient : undefined}
        onNone={() => onChange({ ...paint, visible: !showing })}
        onShader={onShader}
      />
    </>
  );
}

/**
 * The gradient drawn as a bar with its stops sitting on it.
 *
 * Press a stop to choose it and drag it along; press the bar anywhere else for a new stop
 * there (in the colour already there) and keep dragging it; pull a stop well away from the
 * bar and let go to remove it. Delete removes the chosen stop, the arrows nudge it.
 */
function GradientBar({
  stops,
  chosen,
  onChoose,
  onChange,
}: {
  stops: GradientStop[];
  chosen: number;
  onChoose: (index: number) => void;
  onChange: (stops: GradientStop[], index: number) => void;
}) {
  const bar = useRef<HTMLDivElement>(null);
  const [leaving, setLeaving] = useState(false);
  const press = usePress();
  const css = (color: Color) => cssOf(shownColour(press, color));
  const offsetAt = (clientX: number) => {
    const box = bar.current?.getBoundingClientRect();
    // Stops travel inside the bar, their edges at its ends, so the hand is read over that same span.
    return box ? Math.min(1, Math.max(0, (clientX - box.left - STOP / 2) / (box.width - STOP))) : 0;
  };
  const list = [...stops]
    .map((stop) => `${css(stop.color)} ${along(stop.offset)}`)
    .join(', ');

  const hold = (event: React.PointerEvent, start: { stops: GradientStop[]; index: number }) => {
    event.preventDefault();
    const element = event.currentTarget as HTMLElement;
    element.setPointerCapture?.(event.pointerId);
    beginHold();
    let current = start;
    let away = false;
    const move = (e: PointerEvent) => {
      const box = bar.current?.getBoundingClientRect();
      away = !!box && current.stops.length > 2 && (e.clientY < box.top - 28 || e.clientY > box.bottom + 28);
      setLeaving(away);
      current = moveStop(current.stops, current.index, offsetAt(e.clientX));
      onChange(current.stops, current.index);
    };
    // A touch that turns into a scroll ends in pointercancel or a lost capture, never a
    // pointerup; left listening, the stop followed every later touch over the bar and
    // overwrote newer edits (QA 2026-09-24, paint-F29). Only a real let-go removes a stop
    // pulled off the bar.
    const end = (event: PointerEvent) => {
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerup', end);
      element.removeEventListener('pointercancel', end);
      element.removeEventListener('lostpointercapture', end);
      if (away && event.type === 'pointerup') {
        const gone = removeStop(current.stops, current.index);
        onChange(gone.stops, gone.index);
      }
      setLeaving(false);
    };
    element.addEventListener('pointermove', move);
    element.addEventListener('pointerup', end);
    element.addEventListener('pointercancel', end);
    element.addEventListener('lostpointercapture', end);
  };

  return (
    <div
      ref={bar}
      role="slider"
      tabIndex={0}
      aria-label="Gradient stops"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round((stops[chosen]?.offset ?? 0) * 100)}
      className="relative h-[28px] touch-none rounded-[7px] outline-none "
      style={{ background: `linear-gradient(90deg, ${list}), ${CHECKER}`, boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.25)' }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        const made = addStop(stops, offsetAt(event.clientX));
        onChange(made.stops, made.index);
        hold(event, made);
      }}
      onKeyDown={(event) => {
        const stop = stops[chosen];
        if (!stop) return;
        if (event.key === 'Delete' || event.key === 'Backspace') {
          const gone = removeStop(stops, chosen);
          onChange(gone.stops, gone.index);
          event.preventDefault();
          event.stopPropagation();
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          const by = (event.shiftKey ? 10 : 1) / 100;
          const moved = moveStop(stops, chosen, stop.offset + (event.key === 'ArrowLeft' ? -by : by));
          onChange(moved.stops, moved.index);
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      {stops.map((stop, index) => (
        <span
          key={index}
          role="button"
          aria-label={`Stop ${index + 1}, ${Math.round(stop.offset * 100)}%`}
          aria-pressed={index === chosen}
          onPointerDown={(event) => {
            event.stopPropagation();
            if (event.button !== 0) return;
            onChoose(index);
            (event.currentTarget.parentElement as HTMLElement | null)?.focus();
            hold(event, { stops, index });
          }}
          className={`absolute top-1/2 size-[18px] -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-[5px] border-2 ${
            index === chosen ? 'border-white shadow-[0_0_0_2px_var(--accent),0_1px_3px_rgb(0_0_0/0.35)]' : 'border-white shadow-[0_1px_3px_rgb(0_0_0/0.35)]'
          } ${index === chosen && leaving ? 'opacity-30' : ''}`}
          style={{ left: along(stop.offset), background: `linear-gradient(${css(stop.color)}, ${css(stop.color)}), ${CHECKER}` }}
        />
      ))}
    </div>
  );
}

/** A stop's width; it and the bar's colours sit inside the bar, so the end stops never hang off it. */
const STOP = 18;
const along = (offset: number) => `calc(${STOP / 2}px + (100% - ${STOP}px) * ${Math.round(offset * 1000) / 1000})`;

const CHECKER = 'repeating-conic-gradient(#c8c8c8 0% 25%, #ffffff 0% 50%) 50% / 8px 8px';
const cssOf = (color: Color) => `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, ${color.a ?? 1})`;

/** What the well shows: the colour, or the gradient as it runs — through `look` (the press, in a CMYK file). */
function swatchOf(paint: Paint, stops: readonly GradientStop[], look: (color: Color) => Color = (color) => color): string {
  if (paint.type === 'solid') return `linear-gradient(${cssOf(look(paint.color))}, ${cssOf(look(paint.color))}), ${CHECKER}`;
  if (stops.length > 0) return preview(paint, stops.map((stop) => ({ ...stop, color: look(stop.color) })));
  return CHECKER;
}

/** A quarter turn: a linear gradient's ends swung round the middle, the others' rotation. */
function rotated(paint: Paint): Paint {
  if (paint.type === 'linear') {
    // Swung about its own middle, at its own length: rebuilt from the angle alone it came
    // back full width through the box's centre, losing any shortening or offset
    // (QA 2026-09-24, paint-F15).
    const mx = (paint.start.x + paint.end.x) / 2;
    const my = (paint.start.y + paint.end.y) / 2;
    const turn = (p: { x: number; y: number }) => ({ x: mx - (p.y - my), y: my + (p.x - mx) });
    return { ...paint, start: turn(paint.start), end: turn(paint.end) };
  }
  if (paint.type === 'radial' || paint.type === 'conic') return { ...paint, rotation: (((paint.rotation ?? 0) + 90 + 180) % 360) - 180 };
  return paint;
}

/** A colour typed as hex. Anything that is not six hex digits is not a colour yet. */
export function HexField({ hex, onChange, label }: { hex: string; onChange: (hex: string) => void; label: string }) {
  const [typed, setTyped] = useState<string | null>(null);
  const cancelled = useRef(false);
  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      setTyped(null);
      return;
    }
    const hex = typed === null ? null : hexFromTyped(typed);
    if (hex) onChange(hex);
    setTyped(null);
  };
  return (
    <div className="flex h-[26px] min-w-0 flex-1 items-center rounded-[8px] bg-fill-1 px-[8px] field-focus" {...tip('Hex colour')}>
      <span className="text-[11px] text-label-3">#</span>
      <input
        aria-label={label}
        value={typed ?? hex.replace('#', '').toUpperCase()}
        onChange={(event) => setTyped(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            cancelled.current = true;
            setTyped(null);
            event.currentTarget.blur();
          }
          event.stopPropagation();
        }}
        className="tnum h-full w-0 min-w-0 flex-1 bg-transparent pl-[3px] text-[12px] uppercase text-label outline-none"
      />
    </div>
  );
}

/** The CSS that shows what the paint will look like, for the bar above the stops. */
function preview(paint: Paint, stops: readonly GradientStop[]): string {
  const list = [...stops]
    .sort((a, b) => a.offset - b.offset)
    .map((stop) => `rgba(${Math.round(stop.color.r * 255)}, ${Math.round(stop.color.g * 255)}, ${Math.round(stop.color.b * 255)}, ${stop.color.a ?? 1}) ${Math.round(stop.offset * 100)}%`)
    .join(', ');
  const under = 'repeating-conic-gradient(#c8c8c8 0% 25%, #ffffff 0% 50%) 50% / 8px 8px';
  // Where the gradient sits in the box, as the canvas draws it: always centred and upright,
  // the swatch stopped matching the shape once its centre, radius or turn was moved
  // (QA 2026-09-24, paint-F30). CSS cannot turn an ellipse, so a radial gradient shows
  // its turn to the nearest quarter — the radii swap on the odd ones.
  const pct = (n: number) => `${Math.round(n * 1000) / 10}%`;
  if (paint.type === 'radial') {
    const centre = paint.center ?? { x: 0.5, y: 0.5 };
    const radius = paint.radius ?? { x: 0.5, y: 0.5 };
    const odd = Math.abs(Math.round((paint.rotation ?? 0) / 90)) % 2 === 1;
    const [rx, ry] = odd ? [radius.y, radius.x] : [radius.x, radius.y];
    return `radial-gradient(${pct(Math.max(0.001, rx))} ${pct(Math.max(0.001, ry))} at ${pct(centre.x)} ${pct(centre.y)}, ${list}), ${under}`;
  }
  if (paint.type === 'conic') {
    // The canvas sweeps from twelve o'clock turned by the rotation, as CSS's `from` does.
    const centre = paint.center ?? { x: 0.5, y: 0.5 };
    return `conic-gradient(from ${Math.round(paint.rotation ?? 0)}deg at ${pct(centre.x)} ${pct(centre.y)}, ${list}), ${under}`;
  }
  const degrees = paint.type === 'linear' ? angleOf(paint.start, paint.end) + 90 : 90;
  return `linear-gradient(${Math.round(degrees)}deg, ${list}), ${under}`;
}

const angleOf = (start: { x: number; y: number }, end: { x: number; y: number }) => (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI;


/** The first visible solid colour in a list of paints, as hex, or null. */
export function solidOf(paints: readonly Paint[] | undefined): string | null {
  const paint = (paints ?? []).find((item) => item.visible !== false && item.type === 'solid');
  return paint && paint.type === 'solid' ? colorToHex(paint.color) : null;
}

/** The ink that colour was typed as, in a CMYK file, if it was. */
export function solidInkOf(paints: readonly Paint[] | undefined): Cmyk | undefined {
  const paint = (paints ?? []).find((item) => item.visible !== false && item.type === 'solid');
  return paint && paint.type === 'solid' ? paint.color.cmyk : undefined;
}
